import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/button';
import { cssMap, cx } from '@atlaskit/css';
import type { FormFields } from '@atlaskit/feedback-collector/types';
import FeedbackCollector from '@atlaskit/feedback-collector/FeedbackCollector';
import Link from '@atlaskit/link/link';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { dropTargetForExternal } from '@atlaskit/pragmatic-drag-and-drop/adapter/drop-target-for-external';
import { containsFiles } from '@atlaskit/pragmatic-drag-and-drop/utils/contains-files';
import { getFiles } from '@atlaskit/pragmatic-drag-and-drop/utils/get-files';
import { token } from '@atlaskit/tokens';

import type {
	AiSuggestionsPluginOptions,
	SuggestionFeedbackContext,
} from '@atlassian/editor-plugin-ai-suggestions';

import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

// Dedicated SEF (Suggested Edits Feedback) feedback-collector entrypoint.
// Project: https://customerfeedback.atlassian.net/jira/servicedesk/projects/SEF
export const SUGGESTED_EDITS_FEEDBACK_ENTRYPOINT_ID = 'cebd2724-7766-4f78-88b7-f61d07050d48';

// Context custom field id (matches `@atlaskit/feedback-collector` conventions).
const FEEDBACK_CONTEXT_FIELD_ID = 'customfield_10047';

// One of the collector's 10 file slots is reserved for the generated payload.
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const MAX_USER_ATTACHMENTS = 9;

const styles = cssMap({
	attachments: {
		marginBlockStart: token('space.200'),
		marginBlockEnd: token('space.200'),
	},
	heading: {
		color: token('color.text'),
		font: token('font.body'),
		marginBlockEnd: token('space.0'),
		marginBlockStart: token('space.0'),
		paddingInline: token('space.025'),
	},
	dropZone: {
		width: '100%',
		borderWidth: token('border.width'),
		borderStyle: 'dashed',
		borderColor: token('color.border.bold'),
		borderRadius: token('radius.small'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		backgroundColor: token('color.background.neutral.subtle'),
		textAlign: 'center',
		cursor: 'pointer',
		'&:hover': {
			borderColor: token('color.border.selected'),
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	dropZoneActive: {
		borderColor: token('color.border.selected'),
		backgroundColor: token('color.background.selected'),
	},
	hiddenInput: {
		display: 'none',
	},
});

// Cloud id of the internal Atlassian "Hello" dogfooding tenant.
const HELLO_CLOUD_ID = 'a436116f-02ce-4520-8fbb-7301462a1674';

const isHelloTenant = (cloudId: string | undefined): boolean => cloudId === HELLO_CLOUD_ID;

const i18n = defineMessages({
	feedbackTitleHelpful: {
		id: 'editor-features.suggested-edits-feedback.title-helpful',
		defaultMessage: 'You found this suggestion helpful',
		description:
			'Title of the feedback dialog shown after a user marks an AI suggested edit as helpful (thumbs up).',
	},
	feedbackTitleUnhelpful: {
		id: 'editor-features.suggested-edits-feedback.title-unhelpful',
		defaultMessage: 'You found this suggestion unhelpful',
		description:
			'Title of the feedback dialog shown after a user marks an AI suggested edit as unhelpful (thumbs down).',
	},
	summaryPlaceholder: {
		id: 'editor-features.suggested-edits-feedback.summary-placeholder',
		defaultMessage: 'Tell us more about this suggestion',
		description:
			'Placeholder text inside the free-text box of the suggested edits feedback dialog.',
	},
	textAreaLabel: {
		id: 'editor-features.suggested-edits-feedback.text-area-label',
		defaultMessage: 'Tell us more about your experience',
		description: 'Label for the free-text box of the suggested edits feedback dialog.',
	},
	canBeContactedLabel: {
		id: 'editor-features.suggested-edits-feedback.can-be-contacted-label',
		defaultMessage:
			'Yes, Atlassian teams can contact me to learn about my experiences to improve Atlassian products and services. I acknowledge the <a>Atlassian Privacy Policy</a>.',
		description:
			'Label next to the checkbox letting the user opt in to being contacted about their feedback. <a> wraps the Atlassian Privacy Policy link.',
	},
	excludePageContentLabel: {
		id: 'editor-features.suggested-edits-feedback.exclude-page-content-label',
		defaultMessage: 'Do not include page content in product research.',
		description:
			'Label next to the checkbox letting the user stop a copy of the page content being sent with their feedback for Atlassian product research.',
	},
	attachmentsLabel: {
		id: 'editor-features.suggested-edits-feedback.attachments-label',
		defaultMessage: 'Attachments',
		description:
			'Label above the control for attaching files, such as screenshots, to suggested edits feedback.',
	},
	addAttachments: {
		id: 'editor-features.suggested-edits-feedback.add-attachments',
		defaultMessage: 'Add files',
		description: 'Button that opens the file picker to attach files to suggested edits feedback.',
	},
	dropFilesHint: {
		id: 'editor-features.suggested-edits-feedback.drop-files-hint',
		defaultMessage: 'Drag and drop, paste, or click to browse',
		description:
			'Instruction inside the area where users can drag and drop files, paste a screenshot, or click to open the file picker, to attach them to their feedback.',
	},
	removeAttachment: {
		id: 'editor-features.suggested-edits-feedback.remove-attachment',
		defaultMessage: 'Remove',
		description: 'Button that removes one attached file from suggested edits feedback.',
	},
	removeAttachmentLabel: {
		id: 'editor-features.suggested-edits-feedback.remove-attachment-label',
		defaultMessage: 'Remove {fileName}',
		description:
			'Accessible label for the button that removes a named attached file from suggested edits feedback.',
	},
	attachmentsRejected: {
		id: 'editor-features.suggested-edits-feedback.attachments-rejected',
		defaultMessage:
			'{count, plural, one {# file was not added. Files must be under {limit} and you can attach up to {max}.} other {# files were not added. Files must be under {limit} and you can attach up to {max}.}}',
		description:
			'Message shown when attached files are skipped for being too large or exceeding the maximum count.',
	},
	flagTitle: {
		id: 'editor-features.suggested-edits-feedback.flag-title',
		defaultMessage: 'Thanks!',
		description: 'Title of the success flag shown after suggested edits feedback is submitted.',
	},
	flagDescription: {
		id: 'editor-features.suggested-edits-feedback.flag-description',
		defaultMessage: 'Your feedback helps us improve suggested edits.',
		description:
			'Description of the success flag shown after suggested edits feedback is submitted.',
	},
	inlineCommentResolvedFlagTitle: {
		id: 'editor-features.suggested-edits.inline-comment-resolved.flag-title',
		defaultMessage: 'Comment resolved',
		description:
			'Title of the success flag shown after accepting a suggested edit sourced from an inline comment.',
	},
});

const contextLines = (context: SuggestionFeedbackContext): string[] =>
	[
		`sentiment: ${context.sentiment}`,
		`suggestionId: ${context.suggestionId}`,
		context.suggestionType ? `suggestionType: ${context.suggestionType}` : undefined,
		context.interactionPoint ? `interactionPoint: ${context.interactionPoint}` : undefined,
	].filter((line): line is string => Boolean(line));

// Payload + ADF are too large for a JSD field, so Hello users get them as a
// JSON attachment on the feedback ticket instead.
const shouldAttachPayload = (
	cloudId: string | undefined,
	context: SuggestionFeedbackContext | undefined,
): boolean => isHelloTenant(cloudId) && !!context?.suggestion && !!context?.documentAdf;

// The endpoint returns a JSON array of attachment ids.
const extractAttachmentIds = (payload: unknown): string[] =>
	Array.isArray(payload) ? payload.filter((id): id is string => typeof id === 'string') : [];

// The { response, adf } JSON, loadable into the plugin's basic example.
const buildPayloadFile = (context: SuggestionFeedbackContext): File => {
	const json = JSON.stringify(
		{
			response: { ai_feature_output: { items: [context.suggestion] } },
			adf: context.documentAdf,
		},
		null,
		2,
	);
	return new File([json], `suggested-edits-feedback-${context.suggestionId}.json`, {
		type: 'application/json',
	});
};

// Upload files to the feedback collector and return their attachment ids, or none
// on failure. The ids have to be obtained up front: `/v2/feedback` returns an empty
// body, so there is no ticket to attach to afterwards.
const uploadAttachments = async (files: File[]): Promise<string[]> => {
	if (files.length === 0) {
		return [];
	}
	const formData = new FormData();
	files.forEach((file) => formData.append('files', file, file.name));

	try {
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		const response = await fetch(
			`/gateway/api/feedback-collector-api/v2/attachment?entrypointId=${encodeURIComponent(
				SUGGESTED_EDITS_FEEDBACK_ENTRYPOINT_ID,
			)}`,
			{ method: 'POST', body: formData, credentials: 'include' },
		);
		// A 201 with an empty body is possible, so parse defensively rather than
		// letting `response.json()` throw.
		const text = (await response.text()).trim();

		if (!response.ok || !text) {
			return [];
		}
		return extractAttachmentIds(JSON.parse(text));
	} catch {
		return [];
	}
};

// Mirrors `@atlaskit/feedback-collector` defaultProps; used when we take over the
// submission. Keep in sync with `FeedbackCollector`.
const FEEDBACK_FIELDS = {
	summary: 'summary',
	description: 'description',
	customerName: 'customfield_10045',
	canBeContacted: 'customfield_10043',
	enrollInResearch: 'customfield_10044',
	canBeContactedAgree: [{ id: '10109' }],
	canBeContactedDecline: [{ id: '10111' }],
	enrollInResearchAgree: [{ id: '10110' }],
	enrollInResearchDecline: [{ id: '10112' }],
} as const;

// Pasted screenshots arrive unnamed or as a generic "image.png".
const nameClipboardFile = (file: File, index: number, total: number): File => {
	if (file.name && file.name !== 'image.png') {
		return file;
	}
	const extension = file.type.split('/')[1] || 'png';
	const suffix = total > 1 ? `-${index + 1}` : '';
	return new File([file], `screenshot-${Date.now()}${suffix}.${extension}`, { type: file.type });
};

const MAX_SUMMARY_LENGTH = 100;

// Submit comment, opt-ins and the payload attachment as one ticket ourselves (used
// when the collector's own POST is suppressed).
const postSuggestionFeedback = ({
	context,
	formValues,
	name,
	atlassianAccountId,
	attachmentIds,
	hasOptedOutOfDataCollection,
	agreedToResearch,
}: {
	context: SuggestionFeedbackContext;
	formValues: FormFields;
	name: string | undefined;
	atlassianAccountId: string | undefined;
	attachmentIds: string[];
	hasOptedOutOfDataCollection: boolean;
	agreedToResearch: boolean;
}): void => {
	const description = formValues.description ?? '';
	const contextValue = [
		...contextLines(context),
		...(atlassianAccountId ? [`atlassian-account-id: ${atlassianAccountId}`] : []),
		`dataCollectionOptOut: ${hasOptedOutOfDataCollection}`,
	].join('\n');
	const body = {
		feedback: {
			entrypointId: SUGGESTED_EDITS_FEEDBACK_ENTRYPOINT_ID,
			entrypointUiVersion: '1.0.0',
			fields: [
				{ id: FEEDBACK_FIELDS.summary, value: description.slice(0, MAX_SUMMARY_LENGTH) },
				{ id: FEEDBACK_FIELDS.description, value: description },
				{ id: FEEDBACK_FIELDS.customerName, value: name ?? 'unknown' },
				...(formValues.canBeContacted && atlassianAccountId
					? [{ id: 'aaidOrHash', value: atlassianAccountId }]
					: []),
				{
					id: FEEDBACK_FIELDS.canBeContacted,
					value: formValues.canBeContacted
						? FEEDBACK_FIELDS.canBeContactedAgree
						: FEEDBACK_FIELDS.canBeContactedDecline,
				},
				{
					id: FEEDBACK_FIELDS.enrollInResearch,
					value: agreedToResearch
						? FEEDBACK_FIELDS.enrollInResearchAgree
						: FEEDBACK_FIELDS.enrollInResearchDecline,
				},
				{ id: FEEDBACK_CONTEXT_FIELD_ID, value: contextValue },
				...(attachmentIds.length > 0 ? [{ id: 'attachment', value: attachmentIds }] : []),
			],
		},
	};
	// Base64-encode the UTF-8 JSON body (browser-only, runs on submit).
	const postData = btoa(unescape(encodeURIComponent(JSON.stringify(body))));
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	void fetch('/gateway/api/feedback-collector-api/v2/feedback', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ data: postData }),
		credentials: 'include',
	}).catch(() => {
		// Best-effort: don't disrupt the user if the submission fails.
	});
};

const SuggestedEditsFeedbackCollectorComponent = ({
	flags,
	feedbackContext,
	cloudId,
	onClose,
}: {
	flags: FlagsStateContainer;
	feedbackContext?: SuggestionFeedbackContext;
	cloudId?: string;
	onClose: () => void;
}) => {
	const { formatMessage } = useIntl();
	const { userIdForAnalytics, displayName } = useSessionData();

	const fileInputRef = useRef<HTMLInputElement>(null);
	const [{ attachments, rejectedCount }, setAttachmentState] = useState<{
		attachments: File[];
		rejectedCount: number;
	}>({ attachments: [], rejectedCount: 0 });

	const [isDraggingOver, setIsDraggingOver] = useState(false);

	const addFiles = useCallback((picked: File[]) => {
		setAttachmentState((previous) => {
			const room = MAX_USER_ATTACHMENTS - previous.attachments.length;
			const accepted = picked.filter((file) => file.size <= MAX_ATTACHMENT_BYTES).slice(0, room);
			return {
				attachments: [...previous.attachments, ...accepted],
				rejectedCount: picked.length - accepted.length,
			};
		});
	}, []);

	useEffect(() => {
		const onPaste = (event: ClipboardEvent) => {
			const pasted = Array.from(event.clipboardData?.files ?? []);
			if (pasted.length === 0) {
				return;
			}
			event.preventDefault();
			addFiles(pasted.map((file, index) => nameClipboardFile(file, index, pasted.length)));
		};

		document.addEventListener('paste', onPaste);
		return () => document.removeEventListener('paste', onPaste);
	}, [addFiles]);

	const onFilesPicked = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			addFiles(Array.from(event.target.files ?? []));
			// Reset so picking the same file again still fires a change event.
			event.target.value = '';
		},
		[addFiles],
	);

	// `customContent` renders above the form fields, so portal into a host element placed between the description and the opt-ins.
	const attachmentsHost = useRef<HTMLElement | null>(null);
	const [attachmentsContainer, setAttachmentsContainer] = useState<HTMLElement | null>(null);
	const attachmentsAnchorRef = useCallback((element: HTMLElement | null) => {
		attachmentsHost.current?.remove();
		attachmentsHost.current = null;

		const fields = element?.parentElement;
		if (!fields) {
			setAttachmentsContainer(null);
			return;
		}

		// Direct children only, so a nested fieldset cannot capture the insertion point.
		const optInFieldset = Array.from(fields.children).find((child) => child.tagName === 'FIELDSET');
		const host = fields.ownerDocument.createElement('div');
		fields.insertBefore(host, optInFieldset ?? null);
		attachmentsHost.current = host;
		setAttachmentsContainer(host);
	}, []);

	// The drop zone mounts inside a portalled modal, after this component's effects run.
	const unregisterDropTarget = useRef<(() => void) | null>(null);
	const dropZoneRef = useCallback(
		(element: HTMLButtonElement | null) => {
			unregisterDropTarget.current?.();
			unregisterDropTarget.current = null;
			if (!element) {
				return;
			}
			unregisterDropTarget.current = dropTargetForExternal({
				element,
				canDrop: containsFiles,
				onDragEnter: () => setIsDraggingOver(true),
				onDragLeave: () => setIsDraggingOver(false),
				onDrop: ({ source }) => {
					setIsDraggingOver(false);
					addFiles(getFiles({ source }).filter((file): file is File => file !== null));
				},
			});
		},
		[addFiles],
	);

	const onRemoveAttachment = useCallback((index: number) => {
		setAttachmentState((previous) => ({
			attachments: previous.attachments.filter((_, position) => position !== index),
			rejectedCount: 0,
		}));
	}, []);

	const isHello = isHelloTenant(cloudId);
	const canAttachPayload = shouldAttachPayload(cloudId, feedbackContext);

	const onSubmit = (formValues: FormFields) => {
		const researchChecked = formValues.enrollInResearchGroup === true;
		const hasOptedOutOfDataCollection = isHello && researchChecked;
		const agreedToResearch = isHello ? !researchChecked : researchChecked;

		if (feedbackContext) {
			void (async () => {
				// Files the user picked are always sent; the generated payload only when not opted out.
				const files = [
					...(!hasOptedOutOfDataCollection && canAttachPayload
						? [buildPayloadFile(feedbackContext)]
						: []),
					...attachments,
				];
				const attachmentIds = await uploadAttachments(files);

				postSuggestionFeedback({
					context: feedbackContext,
					formValues,
					name: displayName ?? undefined,
					atlassianAccountId: userIdForAnalytics ?? undefined,
					attachmentIds,
					hasOptedOutOfDataCollection,
					agreedToResearch,
				});
			})();
		}

		// Close the dialog once feedback has been sent, then confirm with a flag.
		onClose();
		void flags.showFlag({
			type: 'success-circle',
			title: formatMessage(i18n.flagTitle),
			description: formatMessage(i18n.flagDescription),
		});
	};

	const feedbackTitle =
		feedbackContext?.sentiment === 'good'
			? formatMessage(i18n.feedbackTitleHelpful)
			: formatMessage(i18n.feedbackTitleUnhelpful);

	const attachmentsUi = (
		<Stack space="space.100" xcss={styles.attachments}>
			<Box as="p" xcss={styles.heading}>
				{formatMessage(i18n.attachmentsLabel)}
			</Box>
			<Box xcss={styles.hiddenInput}>
				<input
					ref={fileInputRef}
					type="file"
					multiple
					onChange={onFilesPicked}
					data-testid="suggested-edits-feedback-attachment-input"
					aria-label={formatMessage(i18n.addAttachments)}
				/>
			</Box>
			<Pressable
				ref={dropZoneRef}
				xcss={cx(styles.dropZone, isDraggingOver && styles.dropZoneActive)}
				onClick={() => fileInputRef.current?.click()}
				testId="suggested-edits-feedback-drop-zone"
			>
				<Text size="small" color="color.text.subtlest">
					{formatMessage(i18n.dropFilesHint)}
				</Text>
			</Pressable>
			{attachments.length > 0 && (
				<Stack space="space.050">
					{attachments.map((file, index) => (
						<Inline
							key={`${file.name}-${file.lastModified}-${index}`}
							space="space.100"
							alignBlock="center"
							spread="space-between"
						>
							<Text size="small">{file.name}</Text>
							<Button
								appearance="subtle"
								spacing="compact"
								onClick={() => onRemoveAttachment(index)}
								aria-label={formatMessage(i18n.removeAttachmentLabel, {
									fileName: file.name,
								})}
							>
								{formatMessage(i18n.removeAttachment)}
							</Button>
						</Inline>
					))}
				</Stack>
			)}
			{rejectedCount > 0 && (
				<Text as="p" size="small" color="color.text.danger">
					{formatMessage(i18n.attachmentsRejected, {
						count: rejectedCount,
						limit: '10 MB',
						max: MAX_USER_ATTACHMENTS,
					})}
				</Text>
			)}
		</Stack>
	);

	return (
		<FeedbackCollector
			name={displayName ?? undefined}
			atlassianAccountId={userIdForAnalytics ?? undefined}
			onClose={onClose}
			onSubmit={onSubmit}
			// Blank the entrypoint so the collector skips its own POST and we submit
			// the single, complete ticket ourselves in onSubmit.
			entrypointId=""
			showTypeField={false}
			feedbackTitle={feedbackTitle}
			showRequiredFieldsSummary={false}
			// Contact + product-research checkboxes (contact links to privacy policy).
			canBeContactedLabel={
				<>
					{formatMessage(i18n.canBeContactedLabel, {
						a: (chunks: React.ReactNode[]) => (
							<Link href="https://www.atlassian.com/legal/privacy-policy" target="_blank">
								{chunks}
							</Link>
						),
					})}
				</>
			}
			enrolInResearchLabel={isHello ? formatMessage(i18n.excludePageContentLabel) : undefined}
			customTextAreaLabel={formatMessage(i18n.textAreaLabel)}
			summaryPlaceholder={formatMessage(i18n.summaryPlaceholder)}
			customContent={
				!isHello ? undefined : (
					<>
						<div ref={attachmentsAnchorRef} hidden />
						{/* Rendered after the opt-in checkboxes. Falls back to this slot
					    (the top of the dialog) if the container cannot be resolved. */}
						{attachmentsContainer
							? createPortal(attachmentsUi, attachmentsContainer)
							: attachmentsUi}
					</>
				)
			}
		/>
	);
};

const SuggestedEditsFeedbackCollectorWithFlags = withFlags(
	SuggestedEditsFeedbackCollectorComponent,
);

export interface UseSuggestedEditsPluginOptionsProps {
	cloudId: string;
	contentId: string | undefined;
	contentType: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ConversationAssistant?: React.ComponentType<{ children: React.ReactNode }>;
	flags?: FlagsStateContainer;
	onShowSidebar?: () => void;
}

export interface UseSuggestedEditsPluginOptions {
	FeedbackCollector: React.FC;
	pluginOptions: AiSuggestionsPluginOptions;
}

/**
 * Single source of truth for the AI suggested-edits plugin configuration in the
 * Confluence editor (mirrors the shape of `useLoomPluginOptions` /
 * `useTrackChangesPluginOptions`).
 *
 * Owns both the plugin options **and** the feedback flow: when a user marks a
 * suggestion as helpful/unhelpful, the returned `onSubmitSuggestionFeedback`
 * callback opens the Suggested Edits Feedback (SEF) feedback collector dialog so
 * the user can optionally add free-text feedback before it is submitted.
 *
 * @example
 * const { pluginOptions, FeedbackCollector } = useSuggestedEditsPluginOptions({
 *   cloudId,
 *   contentId,
 *   contentType,
 *   ConversationAssistant,
 *   onShowSidebar,
 * });
 * // pass pluginOptions to aiSuggestionsPlugin, and render <FeedbackCollector />
 */
export const useSuggestedEditsPluginOptions = ({
	cloudId,
	contentId,
	contentType,
	ConversationAssistant,
	flags,
	onShowSidebar,
}: UseSuggestedEditsPluginOptionsProps): UseSuggestedEditsPluginOptions => {
	const [feedbackContext, setFeedbackContext] = useState<SuggestionFeedbackContext>();

	const onClose = useCallback(() => {
		setFeedbackContext(undefined);
	}, []);

	const openFeedbackCollector = useCallback((context: SuggestionFeedbackContext) => {
		setFeedbackContext(context);
	}, []);

	const showInlineCommentResolvedFlag = useCallback(
		(
			suggestion: Parameters<
				NonNullable<AiSuggestionsPluginOptions['onInlineCommentSuggestionAccepted']>
			>[0],
		) => {
			void flags?.showSuccessCircleFlag({
				id: suggestion.id,
				title: <FormattedMessage {...i18n.inlineCommentResolvedFlagTitle} />,
				isAutoDismiss: true,
			});
		},
		[flags],
	);

	const pluginOptions = useMemo(
		(): AiSuggestionsPluginOptions => ({
			contentAri: `ari:cloud:confluence:${cloudId}:${contentType}/${contentId}`,
			onInlineCommentSuggestionAccepted: showInlineCommentResolvedFlag,
			// The plugin can also be enabled by `platform_editor_ai_suggested_edits` alone, which does not
			// include the suggestions sidebar. Exposure is fired by the preset when it enables the plugin.
			onShowSidebar: UNSAFE_expValEqualsNoExposure(
				'platform_editor_ai_content_suggested_edits',
				'isEnabled',
				true,
			)
				? onShowSidebar
				: undefined,
			ConversationAssistant,
			// Open the SEF feedback collector so the user can add additional
			// feedback. The plugin is only enabled when
			// `platform_editor_ai_content_suggested_edits` is on, so this is
			// already behind that gate upstream.
			onSubmitSuggestionFeedback: openFeedbackCollector,
		}),
		[
			cloudId,
			contentId,
			contentType,
			onShowSidebar,
			ConversationAssistant,
			openFeedbackCollector,
			showInlineCommentResolvedFlag,
		],
	);

	const SuggestedEditsFeedbackCollector = useCallback(
		() =>
			feedbackContext ? (
				<SuggestedEditsFeedbackCollectorWithFlags
					feedbackContext={feedbackContext}
					cloudId={cloudId}
					onClose={onClose}
				/>
			) : null,
		[feedbackContext, cloudId, onClose],
	);

	return {
		FeedbackCollector: SuggestedEditsFeedbackCollector,
		pluginOptions,
	};
};
